/**
 * Where an unlocked class key lives between page loads.
 *
 * ── WHY INDEXEDDB AND NOT localStorage ──────────────────────────────────────────────────────────
 *
 * Because a `CryptoKey` can be stored in IndexedDB directly, by structured clone, while remaining
 * **non-extractable**. localStorage holds strings, so caching there would mean writing raw key bytes
 * that any script on the origin could read back. Here the key object is usable for decryption and
 * unreadable as data — including by us.
 *
 * What is NEVER cached: the passphrase, the recovery code, and raw key material. Only the unwrapped
 * `CryptoKey` handle.
 *
 * ── WHAT THIS DOES NOT SURVIVE, STATED PLAINLY ──────────────────────────────────────────────────
 *
 * IndexedDB is per-origin, per-browser-profile. It survives a reload, a browser restart and a reboot. It
 * does NOT survive a different browser, a different machine, a profile wipe, "clear browsing data", or a
 * browser reinstall — and it can be evicted under disk pressure unless `navigator.storage.persist()` is
 * granted. None of that loses data: the wrapped key is in Firestore, so the cost of losing this cache is
 * one passphrase prompt. That is exactly why the design puts the ciphertext in the cloud rather than the
 * names in a local file — a local file has the same fragility with no way back.
 *
 * Keyed by Firebase uid, so a shared browser profile does not hand one teacher another's key, and
 * cleared on sign-out.
 *
 * ── UNTESTED, DELIBERATELY, AND WHY THAT IS TOLERABLE ───────────────────────────────────────────
 *
 * Nothing here has a unit test: node has no IndexedDB and the repo has no shim, and adding a dependency to
 * test a cache whose every failure path is "ask for the passphrase again" is a poor trade. A mutation test
 * confirmed the consequence honestly — removing the extractable tripwire below turns no test red.
 *
 * What makes that acceptable is that the tripwire is the SECOND line. The first is that `unwrapDek` returns
 * a non-extractable key and setup caches that handle rather than the extractable generate-time one, both of
 * which ARE pinned (`keyMaterial.test.ts`). This file would only matter if that invariant were broken
 * upstream. Verify the cross-browser behaviour on a device instead: Safari and iOS are the ones to check.
 */

const DB_NAME = 'luc-vault';
const DB_VERSION = 1;
const STORE = 'classKeys';

/** Is IndexedDB usable at all? Private windows and locked-down profiles can refuse it. */
export const keyCacheAvailable = (): boolean =>
  typeof indexedDB !== 'undefined' && indexedDB !== null;

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Could not open the vault key cache.'));
  });

/** uid is part of the key so one profile cannot serve another teacher's session. */
const cacheKey = (uid: string, classId: string) => `${uid}:${classId}`;

const withStore = async <T,>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('Vault key cache operation failed.'));
    });
  } finally {
    db.close();
  }
};

/**
 * Ask the browser to keep this origin's storage.
 *
 * Chrome grants it to engaged sites, Safari ignores it, and nothing depends on the answer — a refusal
 * costs a passphrase prompt later, not data. Returned so the UI can mention it honestly rather than
 * promising persistence it did not get.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

export async function isPersisted(): Promise<boolean> {
  try {
    return (await navigator.storage?.persisted?.()) ?? false;
  } catch {
    return false;
  }
}

/**
 * Cache an unlocked class key.
 *
 * Every failure is swallowed: the cache is a convenience, and a browser that refuses to store it should
 * cost the teacher a passphrase prompt, never an error dialog on a save that otherwise worked.
 */
export async function cacheClassKey(uid: string, classId: string, key: CryptoKey): Promise<void> {
  if (!keyCacheAvailable()) return;
  try {
    await withStore('readwrite', (s) => s.put(key, cacheKey(uid, classId)));
  } catch (err) {
    console.warn('vault: could not cache the class key (the passphrase will be asked for again)', err);
  }
}

/** The cached key for this class, or null. Null is an ordinary state, not an error. */
export async function readCachedClassKey(uid: string, classId: string): Promise<CryptoKey | null> {
  if (!keyCacheAvailable()) return null;
  try {
    const got = await withStore<CryptoKey | undefined>('readonly', (s) => s.get(cacheKey(uid, classId)) as IDBRequest<CryptoKey | undefined>);
    /**
     * Verify it really is a usable key before handing it back. A structured clone that survived a browser
     * upgrade, or anything else that ended up under this key, would otherwise surface as a decryption
     * failure the teacher reads as "my data is corrupt".
     */
    if (!got || typeof (got as { type?: unknown }).type !== 'string') return null;
    if ((got as CryptoKey).algorithm?.name !== 'AES-GCM') return null;

    /**
     * TRIPWIRE: refuse an EXTRACTABLE key.
     *
     * This file promises the cached key is usable for decryption and unreadable as data, including by us.
     * That is only true of a non-extractable handle. `generateDek` must be extractable so `wrapKey` can
     * export it, and an earlier version of setup cached that very handle — putting a readable key in
     * IndexedDB on the teacher's primary machine, persistently, where any script on the origin could
     * export it. A review caught it; this makes the mistake fail loudly instead of silently holding.
     */
    if ((got as CryptoKey).extractable === true) {
      console.warn('vault: discarding a cached key that is extractable — it should never have been stored');
      await forgetClassKey(uid, classId);
      return null;
    }
    return got;
  } catch (err) {
    console.warn('vault: could not read the cached class key', err);
    return null;
  }
}

/** Forget one class's key. */
export async function forgetClassKey(uid: string, classId: string): Promise<void> {
  if (!keyCacheAvailable()) return;
  try {
    await withStore('readwrite', (s) => s.delete(cacheKey(uid, classId)));
  } catch (err) {
    console.warn('vault: could not clear the cached class key', err);
  }
}

/**
 * Forget every cached key. Call on sign-out and on an explicit lock.
 *
 * Clears the whole store rather than the current uid's entries only: on sign-out we may no longer know
 * which uid was in play, and leaving another account's key behind in a shared profile is the failure this
 * is for.
 */
export async function forgetAllClassKeys(): Promise<void> {
  if (!keyCacheAvailable()) return;
  try {
    await withStore('readwrite', (s) => s.clear());
  } catch (err) {
    console.warn('vault: could not clear the vault key cache', err);
  }
}
